import { Grid } from '@mui/material'
import Loadable from '@react-loadable/revised'
import { useFlags } from 'launchdarkly-react-client-sdk'
import queryString from 'query-string'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'

import AlfiAttackDetailsPage from 'components/alfi/details/AlfiAttackDetailsPage'
// Attacks
import InfraAttackDetailsPage from 'components/attacks/details/InfraAttackDetailsPage'
import LoadableLoading from 'components/common/LoadableLoading'
import Dashboard from 'components/dashboard/Dashboard'
import Footer from 'components/Footer'
import { HelpPage } from 'components/help/HelpPage'
import { NavDrawer } from 'components/navigation/Nav/NavDrawer'
import { useNavigation } from 'components/navigation/Nav/useNavigation'
import Profile from 'components/profile/Profile'
import CompanyDashboardPage from 'components/reliability-management/company-dashboard/CompanyDashboardPage'
import Schedules from 'components/schedules/Schedules'
import TrackedGremlinRoute from 'components/TrackedGremlinRoute'
import { NO_TEAM } from 'context/AuthContext'
import useAuth from 'hooks/use-auth'
import { prefetchPaginationData } from 'hooks/use-gremlin-query'
import useLongRunningApiData from 'hooks/use-long-running-api-data'
import { useManageClusterAccess } from 'hooks/use-manage-cluster-access'
import toast from 'lib/toast'
import { hasSupportAppPermission } from 'support/helpers/utilities'
import { defaultScenarioTab } from 'utils/session'

import NoTeamLandingPage from './NoTeamLandingPage'

const ApiDocs = Loadable({
  loader: () => import(/* webpackChunkName: "ApiDocs" */ 'components/ApiDocs'),
  loading: LoadableLoading
})

// reports
const TeamReport = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "TeamReport" */ 'components/reports/TeamReport'
    ),
  loading: LoadableLoading
})

const CompanyReport = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "Report" */ 'components/reports/company/Report'
    ),
  loading: LoadableLoading
})

const PlanReport = Loadable({
  loader: () =>
    import(/* webpackChunkName: "Report" */ 'components/reports/plan/Report'),
  loading: LoadableLoading
})

const AdminCompanyReport = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CompanyReport" */ 'components/reports/CompanyReport'
    ),
  loading: LoadableLoading
})

const CompanySettings = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CompanySettings" */ 'components/rbac-settings/CompanySettings'
    ),
  loading: LoadableLoading
})

const TeamSettings = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "TeamSettings" */ 'components/rbac-settings/teams/TeamSettings'
    ),
  loading: LoadableLoading
})

const UserPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "UserPage" */ 'components/rbac-settings/users/UserPage'
    ),
  loading: LoadableLoading
})

// New Create Attack Routes
const AlfiAttacksListPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "AlfiAttacksListPage" */ 'components/attacks/list/AlfiAttacksListPage'
    ),
  loading: LoadableLoading
})

const CreateAttackPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CreateAttackPage" */ 'components/attacks/create/CreateAttackPage'
    ),
  loading: LoadableLoading
})

const KubernetesAttackDetailsPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "KubernetesAttackDetailsPage" */ 'components/attacks/details/kubernetes/KubernetesAttackDetailsPage'
    ),
  loading: LoadableLoading
})

const InfraAttackList = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "InfraAttackListPage" */ 'components/attacks/list/InfraAttackListPage'
    ),
  loading: LoadableLoading
})

const ScenariosAttackList = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenariosAttackListPage" */ 'components/attacks/list/ScenariosAttackListPage'
    ),
  loading: LoadableLoading
})

// New Attacks Pages
const CreateScenarios = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CreateScenarioPage" */ 'components/scenarios/create/CreateScenarioPage'
    ),
  loading: LoadableLoading
})

const ExperimentsOverview = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ExperimentsOverview" */ 'components/scenarios/overview/Overview'
    ),
  loading: LoadableLoading
})

const ScenarioDetailsPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenarioDetailsPage" */ 'components/scenarios/detail/ScenarioDetailsPage'
    ),
  loading: LoadableLoading
})

const ScenariosRecommendedDetail = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenarioRecommendedDetail" */ 'components/scenarios/recommended-detail/RecommendedDetail'
    ),
  loading: LoadableLoading
})

const SupportAppLegacy = Loadable({
  loader: () =>
    import(/* webpackChunkName: "SupportAppLegacy" */ 'support/index'),
  loading: LoadableLoading
})

const SupportApp = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SupportApp" */ 'components/support/SupportIndex'
    ),
  loading: LoadableLoading
})

const ScenariosCompanyRecommendedDetail = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenarioCompanyRecommendedDetail" */ 'components/scenarios/recommended-detail/CompanyRecommendedDetail'
    ),
  loading: LoadableLoading
})

const ClientsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ClientsIndex" */ 'components/clients/ClientsIndex'
    ),
  loading: LoadableLoading
})

const HostDetails = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ClientDetails" */ 'components/clients/HostDetails'
    ),
  loading: LoadableLoading
})

const ManageClusterAccessPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ManageClusterAccessPage" */ 'components/kubernetes/access-control/ManageClusterAccessPage'
    ),
  loading: LoadableLoading
})

const ServicesIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ServicesIndex" */ 'components/services/ServicesIndex'
    ),
  loading: LoadableLoading
})

const GettingStartedIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "GettingStartedIndex" */ 'components/getting-started/GettingStartedIndex'
    ),
  loading: LoadableLoading
})

const GameDaysIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "GameDaysIndex" */ 'components/gamedays/GameDaysIndex'
    ),
  loading: LoadableLoading
})

const NotFoundPage = Loadable({
  loader: () =>
    import(/* webpackChunkName: "NotFoundPage" */ 'components/NotFoundPage'),
  loading: LoadableLoading
})

const PaymentIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "PaymentIndex" */ 'components/payment/PaymentIndex'
    ),
  loading: LoadableLoading
})

export default function AuthenticatedRoutes() {
  const {
    RBAC,
    companyDisplayName,
    companyIsAlfiEnabled,
    currentTeam,
    currentTeamId,
    hasCompanyPrivilege,
    hasElevatedCompanyRole,
    hasFaultInjection,
    hasReliabilityManagement,
    isAdmin,
    isPaywallActive,
    session,
    isFreeEdition
  } = useAuth()

  const location = useLocation()

  // Feature flags
  const {
    pricingUsage: isPricingUsageEnabled,
    exposeClientStatus: isExposeClientStatusEnabled,
    createGamedays: isCreateGameDaysEnabled
  } = useFlags()

  const userHasTeams = currentTeamId !== NO_TEAM

  useLongRunningApiData(userHasTeams)

  React.useEffect(() => {
    // Prefetch services because the api call is slow
    // Cannot use usePrefetchPaginationData due to feature flag condition
    const endpoint = `/services/?pageSize=20&teamId=${currentTeamId}`
    if (userHasTeams && hasReliabilityManagement && !isPaywallActive) {
      prefetchPaginationData(endpoint, {})
    }
  }, [currentTeamId, hasReliabilityManagement, userHasTeams, isPaywallActive])

  const { pendingRequestsByAssetId } = useManageClusterAccess({
    poll: true,
    skipTeamRequests: true,
    skipRequestsToTeam: false,
    skipAllAssetsRequests: true,
    enabled: !isPaywallActive
  })

  const { navigationConfigItems } = useNavigation({
    hasFaultInjection,
    hasReliabilityManagement,
    isAdmin,
    isFreeEdition,
    pendingRequestsByAssetId,
    session,
    userHasTeams
  })

  return (
    <>
      <Helmet />
      <NavDrawer
        companyDisplayName={companyDisplayName}
        currentTeamName={currentTeam?.name}
        hasFaultInjection={hasFaultInjection}
        navigationConfigItems={navigationConfigItems}
        session={session}
      >
        <Grid container>
          <Grid item xs={12}>
            <article
              css={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                marginTop: 'calc(var(--info-banner-height) * -1)',
                paddingTop: 'var(--info-banner-height)'
              }}
            >
              {isPaywallActive && (
                <Switch>
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={PaymentIndex}
                    name="Payments"
                    path="/payment/:page"
                  />
                  <Redirect from="*" to="/payment/required" />
                </Switch>
              )}

              {!isPaywallActive && userHasTeams && (
                <Switch>
                  {/*
                IMPORTANT: we need different Switch components here because you CANNOT have Fragments
                inside a Switch component. The Route components must be children of Switch directly!
              */}
                  <TrackedGremlinRoute
                    loggedIn
                    name="Onboarding"
                    path="/onboarding"
                    render={() => <Redirect to="/dashboard" />}
                  />
                  {hasReliabilityManagement && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={CompanyDashboardPage}
                      name="Dashboard"
                      path={['/dashboard']}
                    />
                  )}
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={Dashboard}
                    name="Dashboard"
                    path="/dashboard"
                  />
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={InfraAttackList}
                      name="Attacks List"
                      path="/attacks"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      loggedIn
                      component={CreateAttackPage}
                      name="Create Attack"
                      path="/attacks/new/:targetType?"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={InfraAttackList}
                      name="ILFI Attacks List"
                      path="/attacks/infrastructure"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={AlfiAttacksListPage}
                      name="Application Attacks List"
                      path="/attacks/application"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={ScenariosAttackList}
                      name="Scenarios List"
                      path="/attacks/scenarios"
                    />
                  )}
                  {hasFaultInjection && (
                    <Redirect exact from="/attacks/kubernetes" to="/attacks" />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={KubernetesAttackDetailsPage}
                      name="Kubernetes Attack Details"
                      path="/attacks/kubernetes/:id"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={InfraAttackDetailsPage}
                      name="ILFI Attack Details"
                      path="/attacks/:id"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      loggedIn
                      component={Schedules}
                      name="Scheduled Attacks List"
                      path="/schedules/:tab"
                    />
                  )}
                  {hasFaultInjection && (
                    <Redirect from="/schedules" to="/schedules/attacks" />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={CreateScenarios}
                      name="Create Scenario"
                      path="/scenarios/new/:targetType?"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      name="Edit Scenario"
                      path="/scenarios/edit/:targetType?"
                      render={(props) => (
                        <CreateScenarios isEditing {...props} />
                      )}
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={ScenariosCompanyRecommendedDetail}
                      name="Recommended Company Scenario Details"
                      path="/scenarios/shared/company/:scenarioId/:type?"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={ExperimentsOverview}
                      name="Scenario Overview"
                      path="/scenarios/:tab/company"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={ScenariosRecommendedDetail}
                      name="Recommended Scenario Details"
                      path="/scenarios/recommended/:slug/:type?"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={ExperimentsOverview}
                      name="Scenario Overview"
                      path="/scenarios/:tab"
                    />
                  )}
                  {hasFaultInjection && (
                    <Route // We need to evaluate this each time to get the right tab. Redirect doesn't allow for that so we need a Route + Redirect. Using a plain Route so it isn't double counted.
                      exact
                      component={() => {
                        const tab = defaultScenarioTab()
                        return (
                          <Redirect
                            exact
                            from="/scenarios"
                            to={`/scenarios/${tab}`}
                          />
                        )
                      }}
                      path="/scenarios"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={ExperimentsOverview}
                      name="Status Checks Library"
                      path="/scenarios/status-checks/:tab"
                    />
                  )}
                  <TrackedGremlinRoute
                    loggedIn
                    component={ManageClusterAccessPage}
                    name="Manage Cluster Access"
                    path="/clients/kubernetes/:assetId"
                  />
                  <TrackedGremlinRoute
                    loggedIn
                    component={HostDetails}
                    name="Client Details"
                    path="/clients/hosts/details/:identifier"
                    shouldRenderRoute={
                      isExposeClientStatusEnabled && hasFaultInjection
                    }
                  />
                  <TrackedGremlinRoute
                    loggedIn
                    component={ClientsIndex}
                    name="Clients"
                    path="/clients/:tab?"
                  />

                  {hasReliabilityManagement && (
                    <TrackedGremlinRoute
                      loggedIn
                      component={GettingStartedIndex}
                      name="Getting Started"
                      path="/getting-started"
                    />
                  )}
                  {hasReliabilityManagement && (
                    <TrackedGremlinRoute
                      loggedIn
                      component={ServicesIndex}
                      name="Services"
                      path="/services"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      loggedIn
                      component={GameDaysIndex}
                      name="GameDays"
                      path="/gamedays/:tab?"
                      shouldRenderRoute={isCreateGameDaysEnabled}
                    />
                  )}
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={UserPage}
                    name="User Details"
                    path="/settings/users/:userId"
                    shouldRenderRoute={hasCompanyPrivilege(
                      RBAC.privileges.company.READ_ALL_USERS
                    )}
                  />
                  {!hasElevatedCompanyRole && (
                    <Redirect
                      from="/team-details/:tab?"
                      to={`/settings/team/${currentTeamId}/:tab?`}
                    />
                  )}
                  {/**
                   * The Onboarding team wants to use `https://app.gremlin.com/settings/team/current/configuration`
                   * as a generic link to access a new user's current team when they log in the first time.
                   */}
                  <Redirect
                    from="/settings/team/current/configuration"
                    to={`/settings/team/${currentTeamId}/configuration`}
                  />
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={TeamSettings}
                    name="Team Settings"
                    path="/settings/team/:teamId/:tab?"
                  />
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={CompanySettings}
                    name="Company Settings"
                    path="/settings/:tab?"
                    shouldRenderRoute={hasElevatedCompanyRole}
                  />
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={Profile}
                    name="User Profile"
                    path="/profile/:tab?"
                  />
                  {hasFaultInjection && (
                    <Redirect from="/team-report" to="/reports/team" />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={TeamReport}
                      name="Team Report"
                      path="/reports/team"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={CompanyReport}
                      name="Company Report"
                      path="/reports/company"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={PlanReport}
                      name="Plan Report"
                      path="/reports/plan"
                      shouldRenderRoute={isPricingUsageEnabled}
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      loggedIn
                      component={ScenarioDetailsPage}
                      name="Scenario Run Details"
                      path="/scenarios/detail/:guid/:tab?"
                    />
                  )}
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={AlfiAttackDetailsPage}
                      name="Application Attack Details"
                      path="/attacks/application/:id"
                      shouldRenderRoute={companyIsAlfiEnabled}
                    />
                  )}
                  {hasFaultInjection && (
                    <Redirect from="/alfi/:id" to="/attacks/application/:id" />
                  )}
                  <TrackedGremlinRoute
                    loggedIn
                    component={AdminCompanyReport}
                    name="Company Report"
                    path="/admin/reports"
                    shouldRenderRoute={isAdmin}
                  />
                  {/* This serves as a callback for the oauth integration flow
                  After the user completes the flow with the OAuth provider and our
                  API, the user will be redirected from the API back to the webapp
                  at this path. The URL will contain a query parameter called `msg`
                  which contains either a sucecss or error message to display to the user
               */}
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    path="/oauth-integration"
                    render={() => {
                      const message = queryString.parse(location.search).msg

                      if (message)
                        toast({
                          type: 'info',
                          text: message
                        })

                      return <Redirect to="/settings" />
                    }}
                  />
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    path="/jira"
                    render={() => {
                      window.message = queryString.parse(location.search).msg
                      window.error = queryString.parse(location.search).error
                      window.close()
                      return null
                    }}
                  />
                  {hasFaultInjection && (
                    <TrackedGremlinRoute
                      exact
                      loggedIn
                      component={ApiDocs}
                      name="API Docs"
                      path="/api"
                    />
                  )}
                  <TrackedGremlinRoute
                    loggedIn
                    component={HelpPage}
                    name="Help"
                    path="/help"
                  />
                  {/* TODO rename legacy support routes /support/legacy/ and change the new routes to /support */}
                  <Route
                    component={SupportAppLegacy}
                    path="/support"
                    shouldRenderRoute={hasSupportAppPermission(session)}
                  />
                  <Route
                    component={SupportApp}
                    path="/supportnew"
                    shouldRenderRoute={hasSupportAppPermission(session)}
                  />
                  {/* these make it so the user doesn't get a 404 if they hit the back button after logging in */}
                  <Redirect from="/login" to="/" />
                  {hasReliabilityManagement && (
                    <Redirect from="/" to="/getting-started" />
                  )}
                  {!hasReliabilityManagement && (
                    <Redirect from="/" to="/dashboard" />
                  )}
                  <Redirect from="/users/sso/*" to="/" />
                  {/* make sure anyone landing on the payment pages when the paywall is down gets redirected instead of a 404 */}
                  <Redirect from="/payment/*" to="/" />

                  <Route path="*">
                    <NotFoundPage />
                  </Route>
                </Switch>
              )}

              {!isPaywallActive && !userHasTeams && (
                <Switch>
                  <Route exact component={NoTeamLandingPage} path="/noteam" />
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={Profile}
                    name="User Profile"
                    path="/profile/:tab?"
                  />
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={TeamSettings}
                    name="Team Settings"
                    path="/settings/team/:teamId/:tab?"
                    shouldRenderRoute={hasElevatedCompanyRole}
                  />
                  <TrackedGremlinRoute
                    exact
                    loggedIn
                    component={CompanySettings}
                    name="Company Settings"
                    path="/settings/:tab?"
                    shouldRenderRoute={hasElevatedCompanyRole}
                  />

                  {/* make sure anyone landing on the payment pages when the paywall is down gets redirected instead of a 404 */}
                  <Redirect from="/payment/*" to="/" />

                  {/* everything else redirects to /noteam */}
                  <Redirect from={/^\/(?!profile)/} to="/noteam" />
                </Switch>
              )}
            </article>
          </Grid>
          <Grid item css={{ marginTop: 'auto' }} xs={12}>
            <Footer />
          </Grid>
        </Grid>
      </NavDrawer>
    </>
  )
}
